import React from 'react';
import Home from './v2/modules/home/.';
import Locations from './components/Locations/locations';
import Users from './v2/shared/components/users/users';
import Settings from './components/settings/settings';
import SisenseDashboard from './components/sisense-dashboard';
import Incentives from './components/dashboard/incentiveengine/incentives';
import NotFound from './components/NotFound';
import Todos from './components/users/HorizonFriends';
import loadData from './helpers/loadData';
import UserAgreement from './components/legal/useragreement';
import CheckEvents from './components/users/checkEvents';
import Management from './components/management/management';
import Feeds from './v2/modules/Feeds/Feeds';
import { Message } from './v2/modules/messaging';
import { ManageRoleAndPermission } from './components/manage-roles-permissions';
import RolesDetail from './components/roles-detail';
import Facility from './components/Locations/facility';
import FacilityUsers from './components/Locations/facility-users';
import Unit from './components/Locations/unit';
import { ROLES } from './constants';
import { ManageLocations } from './components/manage-locations';
import { LocationSettings } from './components/manage-locations/edit';
import { Navigate } from 'react-router-dom';
import Profile from './components/management/staff/staff-profile/profile';
import Learner from './components/users/learner/Learner';
import { ContactsListContainer, LearnerProfileContainer } from './components/voice-call/containers';
import { ResumeBuilder } from './v2/modules/resume/resume-builder';
import SuperAdminModule from './components/superadmin-module';
import FriendsAndFamilyModule from './components/friends-and-family-module';
import LearningPlans from './v2/modules/learning-plans/learning-plans';
import LearningPlanDetails from './v2/modules/learning-plans/details/learning-plan-details';
import LearningPlanProgress from './v2/modules/learning-plans/progress/learning-plan-progress';
import PrivacyPolicy from './components/legal/privacypolicy';
import Copyright from './components/legal/copyright';
import TermsOfUse from './components/legal/termsofuse';
import useUnleashHook from './v2/core/unleash/useUnleashHook';
import { UnleashService } from './v2/services';
import { AUTH_CONSTANTS } from './v2/core/auth/constants/auth-constants';
import { Audit as FeedsAudit } from './v2/modules/Feeds/components/Audit';

const AuthGuardsController = React.lazy(() =>
  import('./v2/core/auth-guards').then((module) => ({ default: module.AuthGuardsController }))
);
const ConfigureLearningPlanPillarRules = React.lazy(() =>
  import('./v2/modules/learning-plans/configure-rules/configure-rules').then((module) => ({ default: module.default }))
);
const LearningPlanReport = React.lazy(() =>
  import('./v2/modules/learning-plans/report/learning-plan-report').then((module) => ({ default: module.default }))
);

const AuthGuardsSSOController = () => {
  return <AuthGuardsController isSSOOnly={true} />;
};

const lastVisitedPath = sessionStorage.getItem(AUTH_CONSTANTS.LAST_VISITED_PATH);

const Messaging = (props) => {
  const { isFlagEnabled } = useUnleashHook(UnleashService.FLAGS.DISABLE_V1_MESSAGING);
  if (isFlagEnabled()) {
    return null;
  }
  
  // If flag is disabled, render the Message component normally
  return <Message {...props} />;
};

const RootDirection = () => {
  const persistLastVisitedPath = useUnleashHook(UnleashService.FLAGS.PERSIST_LAST_VISITED_PATH).isFlagEnabled();
   return lastVisitedPath && persistLastVisitedPath
    ? <Navigate to={lastVisitedPath} replace /> 
    : <Navigate to="/home" replace />;
}

//this provides a list of static routes to be used by App.js and server.js to populate routes with server side rendering
export const SuperAdminRoutes = [
  {
    path: '/',
    component: RootDirection,
  },
  {
    path: '/home',
    component: Home,
  },
  {
    path: '/locations',
    component: Locations,
  },
  {
    path: '/users',
    component: Users,
  },
  {
    path: '/settings',
    component: Settings,
  },
  {
    path: '/users/learner/:id',
    component: Learner,
  },
  {
    path: '/users/:id',
    component: Profile,
  },
  {
    path: '/incentives',
    component: Incentives,
  },
  {
    path: '/manage',
    component: Management,
  },
  {
    path: '/manageRoles',
    component: ManageRoleAndPermission,
  },
  {
    path: '/manageRoles/:roleName/:roleId',
    component: RolesDetail,
  },
  {
    path: '/messages',
    component: Messaging,
  },
  {
    path: '/messages/:location/:threadId',
    component: Messaging,
  },
  {
    path: '/feeds',
    exact: true,
    component: Feeds,
  },
  {
    path: '/feeds/audit',
    exact: true,
    component: FeedsAudit,
  },
  {
    path: '/manage/:location',
    component: Management,
  },
  {
    path: '/manage/:location/form',
    component: Management,
  },
  {
    path: '/manage/:location/:housingUnit',
    component: Management,
  },
  {
    path: '/outcomes-and-progress',
    component: Locations,
  },
  {
    path: '/outcomes-and-progress/:locationName/:locationId',
    component: Facility,
  },
  {
    path: '/outcomes-and-progress/:locationName/:locationId/users',
    component: FacilityUsers,
  },
  {
    path: '/outcomes-and-progress/:locationName/:locationId/:unitName/:unitId',
    component: Unit,
  },
  {
    path: '/checkevents',
    component: CheckEvents,
  },
  {
    path: '/roles',
    component: NotFound,
  },
  {
    path: '/todos',
    component: Todos,
    loadData: () => loadData('people'),
  },
  {
    path: 'useragreement',
    component: UserAgreement,
  },
  {
    path: '/manageLocations/:location',
    component: LocationSettings,
    exact: true,
  },
  {
    path: '/manageLocations',
    component: ManageLocations,
    exact: true,
  },
  {
    path: '/voiceCalls',
    component: ContactsListContainer,
    exact: true,
  },
  {
    path: '/voiceCalls/learnerProfile',
    component: LearnerProfileContainer,
    exact: true,
  },
  {
    path: '/superadmin',
    component: SuperAdminModule,
    exact: true,
  },
  {
    path: '/learning-plans/:planKey/pillars/:pillarName/configure-rules',
    component: ConfigureLearningPlanPillarRules,
  },
  {
    path: '/learning-plans/:planKey/report/users/:userId',
    component: LearningPlanReport,
    exact: true,
  },
  {
    path: '/learning-plans',
    component: LearningPlans,
    exact: true,
  },
  {
    path: '/learning-plans/:key',
    component: LearningPlanDetails,
    exact: true,
  },
  {
    path: '/learning-plans/:key/progress',
    component: LearningPlanProgress,
    exact: true,
  },
  {
    path: '/ffMessaging',
    component: FriendsAndFamilyModule,
    exact: true,
  },
  {
    component: NotFound,
  },
];

export const AdminRoutes = [
  {
    path: '/',
    component: RootDirection,
  },
  {
    path: '/home',
    component: Home,
  },
  {
    path: '/locations',
    component: Locations,
  },
  {
    path: '/users',
    component: Users,
  },
  {
    path: '/settings',
    component: Settings,
  },
  {
    path: '/users/learner/:id',
    component: Learner,
  },
  {
    path: '/users/:id',
    component: Profile,
  },
  {
    path: '/incentives',
    component: Incentives,
  },
  {
    path: '/manage',
    component: Management,
  },
  {
    path: '/messages',
    component: Messaging,
  },
  {
    path: '/messages/:location/:threadId',
    component: Messaging,
  },
  {
    path: '/feeds',
    exact: true,
    component: Feeds,
  },
  {
    path: '/feeds/audit',
    exact: true,
    component: FeedsAudit,
  },
  {
    path: '/manage/:location',
    component: Management,
  },
  {
    path: '/manage/:location/form',
    component: Management,
  },
  {
    path: '/manage/:location/:housingUnit',
    component: Management,
  },
  {
    path: '/outcomes-and-progress',
    component: Locations,
  },
  {
    path: '/outcomes-and-progress/:locationName/:locationId',
    component: Facility,
  },
  {
    path: '/outcomes-and-progress/:locationName/:locationId/users',
    component: FacilityUsers,
  },
  {
    path: '/outcomes-and-progress/:locationName/:locationId/:unitName/:unitId',
    component: Unit,
  },
  {
    path: '/checkevents',
    component: CheckEvents,
  },
  {
    path: '/roles',
    component: NotFound,
  },
  {
    path: '/todos',
    component: Todos,
    loadData: () => loadData('people'),
  },
  {
    path: 'useragreement',
    component: UserAgreement,
  },
  {
    path: '/voiceCalls',
    component: ContactsListContainer,
  },
  {
    path: '/voiceCalls/learnerProfile',
    component: LearnerProfileContainer,
  },

  {
    path: '/manageLocations/:location',
    component: LocationSettings,
  },
  {
    path: '/manageLocations',
    component: ManageLocations,
  },
  {
    component: NotFound,
  },
];

export const AttorneyRoutes = [
  {
    path: '/',
    component: RootDirection,
    exact: true,
  },
  {
    path: '/home',
    exact: true,
    component: Home,
  },
  {
    path: '/messages',
    exact: true,
    component: Messaging,
  },
  {
    path: '/messages/:location/:threadId',
    exact: true,
    component: Messaging,
  },
  {
    path: '/feeds',
    exact: true,
    component: Feeds,
  },
  {
    path: 'useragreement',
    exact: true,
    component: UserAgreement,
  },
  {
    component: NotFound,
  },
];

export const TeacherRoutes = [
  {
    path: '/',
    component: RootDirection,
    exact: true,
  },
  {
    path: '/messages',
    exact: true,
    component: Messaging,
  },
  {
    path: '/messages/:location/:threadId',
    exact: true,
    component: Messaging,
  },
  {
    path: '/feeds/audit',
    exact: true,
    component: FeedsAudit,
  },
  {
    path: '/feeds',
    exact: true,
    component: Feeds,
  },
  
  {
    path: 'useragreement',
    exact: true,
    component: UserAgreement,
  },
  {
    component: NotFound,
  },
];

export const PermittedRoutes = [
  {
    path: '/',
    component: RootDirection,
    exact: true,
  },
  {
    path: '/home',
    exact: true,
    component: Home,
  },
  {
    path: '/analytics',
    exact: true,
    component: SisenseDashboard,
    permissions: ['View_Analytics', 'View_Progress_And_Outcomes'],
  },
  {
    path: '/voiceCalls',
    component: ContactsListContainer,
    exact: true,
  },
  {
    path: '/voiceCalls/learnerProfile',
    component: LearnerProfileContainer,
    exact: true,
  },
  {
    path: '/locations',
    component: Locations,
    permissions: ['Access_User_And_Location_Management'],
  },
  {
    path: '/users',
    exact: true,
    component: Users,
    permissions: ['Access_User_And_Location_Management'],
  },
  {
    path: '/settings',
    component: Settings,
  },
  {
    path: '/users/learner/:id',
    component: Learner,
  },
  {
    path: '/users/:id',
    exact: true,
    component: Profile,
    permissions: ['Access_User_And_Location_Management'],
  },
  {
    path: '/incentives',
    component: Incentives,
  },
  {
    path: '/manage',
    component: Management,
    permissions: ['Access_User_And_Location_Management'],
  },
  {
    path: '/manageRoles',
    component: ManageRoleAndPermission,
    roleTypes: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/manageRoles/:roleName/:roleId',
    component: RolesDetail,
    roleTypes: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/messages',
    component: Messaging,
    permissions: ['Access_Messages'],
  },
  {
    path: '/messages/:location/:threadId',
    component: Messaging,
    permissions: ['Access_Messages'],
  },
  {
    path: '/feeds',
    exact: true,
    component: Feeds,
    permissions: ['Access_Messages']
  },
  {
    path: '/feeds/audit',
    exact: true,
    component: FeedsAudit,
    permissions: ['Access_Messages']
  },
  {
    path: '/manage/:location',
    component: Management,
    permissions: ['Access_User_And_Location_Management'],
  },
  {
    path: '/manage/:location/form',
    component: Management,
    permissions: ['Access_User_And_Location_Management'],
  },
  {
    path: '/manage/:location/:housingUnit',
    component: Management,
    permissions: ['Access_User_And_Location_Management'],
  },
  {
    path: '/outcomes-and-progress',
    component: Locations,
    permissions: ['View_Progress_And_Outcomes'],
  },
  {
    path: '/outcomes-and-progress/:locationName/:locationId',
    component: Facility,
    permissions: ['View_Aggregate_Data_At_Facility'],
  },
  {
    path: '/outcomes-and-progress/:locationName/:locationId/users',
    component: FacilityUsers,
    permissions: [['View_Aggregate_Data_At_Facility', 'View_Aggregate_Data_At_Hu']],
  },
  {
    path: '/outcomes-and-progress/:locationName/:locationId/:unitName/:unitId',
    permissions: [['View_Aggregate_Data_At_Facility', 'View_Aggregate_Data_At_Hu']],
    component: Unit,
  },
  {
    path: '/checkevents',
    component: CheckEvents,
  },
  {
    path: '/roles',
    component: NotFound,
  },
  {
    path: '/todos',
    exact: true,
    component: Todos,
    loadData: () => loadData('people'),
  },
  {
    path: 'useragreement',
    component: UserAgreement,
  },
  {
    path: '/manageLocations/:location',
    component: LocationSettings,
  },
  {
    path: '/manageLocations',
    component: ManageLocations,
  },
  {
    path: '/resume/:id',
    component: ResumeBuilder,
    roleTypes: [ROLES.APDS_HORIZON_SUPER_ADMIN, ROLES.APDS_HORIZON_FACILITY_ADMIN, ROLES.SUPER_ADMIN, ROLES.STAFF, ROLES.TEACHER],
  },
  {
    path: '/superadmin',
    component: SuperAdminModule,
  },
  {
    path: '/learning-plans/:planKey/pillars/:pillarName/configure-rules',
    component: ConfigureLearningPlanPillarRules,
    roleTypes: [ROLES.SUPER_ADMIN],
  },
  {
    path: '/learning-plans/:planKey/report/users/:userId',
    component: LearningPlanReport,
    exact: true,
  },
  {
    path: '/learning-plans',
    component: LearningPlans,
  },
  {
    path: '/learning-plans/:key',
    component: LearningPlanDetails,
  },
  {
    path: '/learning-plans/:key/progress',
    component: LearningPlanProgress,
    exact: true,
  },
  {
    path: '/ffMessaging',
    component: FriendsAndFamilyModule,
  },
  {
    component: NotFound,
  },
];

export const getPublicRoutes = () => {
  return [
    {
      path: '/useragreement',
      component: UserAgreement,
      exact: true,
    },
    {
      path: '/termsofuse',
      component: TermsOfUse,
      exact: true,
    },
    {
      path: '/copyright',
      component: Copyright,
      exact: true,
    },
    {
      path: '/privacypolicy',
      component: PrivacyPolicy,
      exact: true,
    },
    {
      path: '/auth-guards',
      component: AuthGuardsController,
      exact: true,
    },
    {
      path: '/sso/auth-guards',
      component: AuthGuardsSSOController,
      exact: true,
    },
  ];
};

export default SuperAdminRoutes;
